/**
 * voiceService.js
 * Abstracts Web Speech API and Whisper fallback for voice input.
 *
 * Whisper transcription is proxied through the Aidey backend (/api/voice/transcribe)
 * so the OpenAI API key is never exposed to the browser.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Speech Recognition ─────────────────────────────────────────────────────────

/**
 * Returns true if the browser natively supports the Web Speech API.
 */
export function isSpeechRecognitionAvailable() {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * Creates and configures a SpeechRecognition instance.
 *
 * @param {object} opts
 * @param {function} opts.onResult        — final transcript string
 * @param {function} opts.onInterim       — interim transcript string (live preview)
 * @param {function} opts.onEnd           — called when recognition ends
 * @param {function} opts.onError         — called with error message string
 * @param {string}   [opts.lang='en-GB']  — BCP-47 language tag
 * @returns {SpeechRecognition|null}
 */
export function createSpeechRecognition({ onResult, onInterim, onEnd, onError, lang = 'en-GB' }) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return null;

  const recognition = new SpeechRecognition();
  recognition.lang = lang;
  // continuous = true so the recognizer keeps listening across natural pauses
  // instead of finalizing and stopping after the first utterance. The caller's
  // silence timer is the real stop trigger (see useVoiceInput.js).
  recognition.continuous = true;
  recognition.interimResults = true;

  // In continuous mode onresult fires repeatedly as more phrases finalize, so we
  // accumulate finalized segments here (append) rather than treating each event as
  // the whole transcript. Both callbacks always receive the complete text so far.
  let finalTranscript = '';
  recognition.onresult = (event) => {
    let interim = '';
    let gotFinal = false;
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += t + ' ';
        gotFinal = true;
      } else {
        interim += t;
      }
    }
    const combined = (finalTranscript + interim).trim();
    // Live preview = everything finalized so far plus the current interim tail.
    // This also drives the caller's silence-timer reset on each new result.
    if (combined && onInterim) onInterim(combined);
    // Deliver the accumulated final whenever a new segment finalizes.
    if (gotFinal && onResult) onResult(finalTranscript.trim());
  };

  recognition.onerror = (event) => {
    if (onError) onError(event.error || 'Speech recognition error');
  };

  recognition.onend = () => {
    if (onEnd) onEnd();
  };

  return recognition;
}

// ── MediaRecorder (Whisper fallback) ──────────────────────────────────────────

/**
 * Starts recording via MediaRecorder.
 * Resolves with { stop, stream } where stop() returns a Promise<Blob>.
 *
 * @returns {Promise<{ stop: () => Promise<Blob>, stream: MediaStream }>}
 */
export async function startMediaRecorder() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = new MediaRecorder(stream);
  const chunks = [];

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  recorder.start();

  return {
    stream,
    stop: () =>
      new Promise((resolve) => {
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
          stream.getTracks().forEach((t) => t.stop());
          resolve(blob);
        };
        recorder.stop();
      }),
  };
}

/**
 * Sends an audio blob to the backend proxy (/api/voice/transcribe), which
 * calls Whisper on the server. The OpenAI key never leaves the backend.
 *
 * @param {Blob} blob — audio data
 * @returns {Promise<string>} transcript text
 */
export async function transcribeWithWhisper(blob) {
  const { getAccessToken } = await import('../api/client');
  const token = getAccessToken() || '';

  const formData = new FormData();
  formData.append('audio', blob, 'recording.webm');

  const response = await fetch(`${API_BASE}/api/voice/transcribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || `Transcription failed (${response.status})`);
  }

  const data = await response.json();
  return data.text || '';
}

// ── Intent detection ───────────────────────────────────────────────────────────

const INTENT_TYPES = ['create_task', 'create_meeting', 'create_invoice', 'create_note', 'create_contact', 'general_chat'];

/**
 * Posts a voice transcript to /api/aidey with a structured system prompt.
 * Returns a parsed intent object or null on failure.
 *
 * @param {string} transcript — the voice transcript
 * @param {string} apiBase    — base URL e.g. 'http://localhost:5001'
 * @returns {Promise<{ intent: string, summary: string, data: object }|null>}
 */
export async function detectIntent(transcript, apiBase) {
  const today = new Date().toISOString().split('T')[0];

  const systemPrompt = `You are an intent parser. Today's date is ${today}.

Given the user's message, respond with ONLY a JSON object (no markdown, no explanation):
{
  "intent": "<one of: ${INTENT_TYPES.join(', ')}>",
  "summary": "<concise one-line summary of the action>",
  "data": {
    // for create_task: { title, dueDate (ISO 8601 or null), priority ("low"|"normal"|"high") }
    // for create_meeting: { title, datetime (ISO 8601 or null), duration (minutes or null) }
    // for create_invoice: { client, amount (number or null), description }
    // for create_note: { title, body }
    // for create_contact: { name, email (or null), phone (or null) }
    // for general_chat: {}
  }
}

If unsure, use "general_chat". Always produce valid JSON.`;

  try {
    const { api } = await import('../api/client');
    const res = await api('/api/aidey', {
      method: 'POST',
      body: JSON.stringify({
        messages: [{ role: 'user', content: transcript }],
        systemPrompt,
        jsonOnly: true,
      }),
    });

    if (!res || !res.ok) return null;

    const raw = await res.json();
    // Accept various shapes the backend might return
    const text = raw?.reply || raw?.message || raw?.text || '';

    // Strip markdown code fences if present
    const cleaned = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
    const parsed = JSON.parse(cleaned);

    if (!parsed.intent || !INTENT_TYPES.includes(parsed.intent)) return null;
    return parsed;
  } catch {
    return null;
  }
}
